import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography, Box, Button } from '@mui/material';
import { connectAppsStyles } from './styles';
import { useConnectApps } from './context';
import { CONNECT_APP_SECTIONS } from './context';
import { useTranslation } from 'src/text';
import { LocationSelect } from 'src/components/location-select';

type ConnectLabelsType = {
  connectYourApps?: string;
  connectAppsSubtitle?: string;
  socialmedia?: string;
  business?: string;
  facebookName?: string;
  facebookDescription?: string;
  twitterName?: string;
  twitterDescription?: string;
  instagramName?: string;
  instagramDescription?: string;
  tiktokName?: string;
  tiktokDescription?: string;
  cloverName?: string;
  cloverDescription?: string;
  connect?: string;
  disconnect?: string;
  [key: string]: string | undefined;
};

type AppType = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
};

type SectionType = {
  label: string;
  apps: AppType[];
};

const ConnectAppItem: React.FC<{
  app: AppType;
  isConnected: boolean;
  isLoading: boolean;
  connectLabels: ConnectLabelsType;
  onClick: () => Promise<void>;
}> = ({ app, isConnected, isLoading, connectLabels, onClick }) => (
  <Box sx={connectAppsStyles.appItem}>
    <Typography variant="subtitle1">
      {connectLabels[`${app.id}Name`] || app.name}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {connectLabels[`${app.id}Description`] || app.description}
    </Typography>
    <Button
      variant={isConnected ? 'outlined' : 'contained'}
      color={isConnected ? 'secondary' : 'primary'}
      onClick={onClick}
      sx={{ mt: 1, minWidth: 120 }}
      disabled={!app.enabled || isLoading}>
      {isLoading ? (
        <>
          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
          {isConnected
            ? connectLabels.disconnect || 'Disconnect'
            : connectLabels.connect || 'Connect'}
        </>
      ) : isConnected ? (
        connectLabels.disconnect || 'Disconnect'
      ) : (
        connectLabels.connect || 'Connect'
      )}
    </Button>
  </Box>
);

const ConnectAppSection: React.FC<{
  section: SectionType;
  connectedApps: string[];
  loadingApp: string | null;
  setLoadingApp: (id: string | null) => void;
  connectApp: (id: string) => Promise<void>;
  disconnectApp: (id: string) => Promise<void>;
  connectLabels: ConnectLabelsType;
}> = ({
  section,
  connectedApps,
  loadingApp,
  setLoadingApp,
  connectApp,
  disconnectApp,
  connectLabels,
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ mb: 0 }}>
      {connectLabels[section.label.toLowerCase().replace(/ /g, '')] ||
        section.label}
    </Typography>
    {section.label === 'Social Media' && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: 'block' }}>
        Powered by{' '}
        <a
          href="https://getlate.dev"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1e88e5', textDecoration: 'underline' }}>
          Late
        </a>
      </Typography>
    )}
    <Box sx={connectAppsStyles.appsList}>
      {section.apps.map(app => {
        const isConnected = connectedApps.includes(app.id);
        const isLoading = loadingApp === app.id;
        const handleClick = async () => {
          setLoadingApp(app.id);
          try {
            if (isConnected) {
              await disconnectApp(app.id);
            } else {
              await connectApp(app.id);
            }
          } finally {
            setLoadingApp(null);
          }
        };
        return (
          <ConnectAppItem
            key={app.id}
            app={app}
            isConnected={isConnected}
            isLoading={isLoading}
            connectLabels={connectLabels}
            onClick={handleClick}
          />
        );
      })}
    </Box>
  </Box>
);

const ConnectAppsContainer: React.FC = () => {
  const { connectedApps, connectApp, disconnectApp } = useConnectApps();
  const [loadingApp, setLoadingApp] = useState<string | null>(null);
  const translation = useTranslation() as { connectLabels: ConnectLabelsType };
  const connectLabels: ConnectLabelsType = translation.connectLabels || {};

  return (
    <Box sx={connectAppsStyles.container}>
      <LocationSelect />
      <Typography mt={4} variant="h4" gutterBottom>
        {connectLabels.connectYourApps || 'Connect Your Apps'}
      </Typography>
      <Typography mb={4} variant="body1" gutterBottom>
        {connectLabels.connectAppsSubtitle ||
          'Integrate with third-party platforms to unlock more features.'}
      </Typography>
      {CONNECT_APP_SECTIONS.map(section => (
        <ConnectAppSection
          key={section.label}
          section={section}
          connectedApps={connectedApps}
          loadingApp={loadingApp}
          setLoadingApp={setLoadingApp}
          connectApp={connectApp}
          disconnectApp={disconnectApp}
          connectLabels={connectLabels}
        />
      ))}
    </Box>
  );
};

export { ConnectAppsContainer };
